'use client';

import dynamic from 'next/dynamic';
import { Component, useEffect, useState, type ReactNode } from 'react';

import { isWebGLAvailable } from '@/lib/webgl-support';
import { BRAND_PINK } from "@/lib/brand";

const CI_GRADIENT = `linear-gradient(135deg, #1a1028, #3d1028, , #3d4557)`;

const lazyComponents: Record<string, React.ComponentType<any>> = {
  aurora: dynamic(() => import('./components/Aurora'), { ssr: false }),
  silk: dynamic(() => import('./components/Silk'), { ssr: false }),
  liquidEther: dynamic(() => import('./components/LiquidEther'), { ssr: false }),
  darkVeil: dynamic(() => import('./components/DarkVeil'), { ssr: false }),
  lightPillar: dynamic(() => import('./components/LightPillar'), { ssr: false }),
  floatingLines: dynamic(() => import('./components/FloatingLines'), { ssr: false }),
  pixelBlast: dynamic(() => import('./components/PixelBlast'), { ssr: false }),
  colorBends: dynamic(() => import('./components/ColorBends'), { ssr: false }),
  lineWaves: dynamic(() => import('./components/LineWaves'), { ssr: false }),
  softAurora: dynamic(() => import('./components/SoftAurora'), { ssr: false }),
  plasma: dynamic(() => import('./components/Plasma'), { ssr: false }),
  grainient: dynamic(() => import('./components/Grainient'), { ssr: false }),
  faultyTerminal: dynamic(() => import('./components/FaultyTerminal'), { ssr: false }),
  rippleGrid: dynamic(() => import('./components/RippleGrid'), { ssr: false }),
  dotGrid: dynamic(() => import('./components/DotGrid'), { ssr: false }),
  threads: dynamic(() => import('./components/Threads'), { ssr: false }),
  hyperspeed: dynamic(() => import('./components/Hyperspeed'), { ssr: false }),
  iridescence: dynamic(() => import('./components/Iridescence'), { ssr: false }),
  waves: dynamic(() => import('./components/Waves'), { ssr: false }),
  gridDistortion: dynamic(() => import('./components/GridDistortion'), { ssr: false }),
  shapeGrid: dynamic(() => import('./components/ShapeGrid'), { ssr: false }),
};

function FallbackGradient() {
  return (
    <div
      className="absolute inset-0 w-full h-full"
      style={{ background: CI_GRADIENT }}
    />
  );
}

/**
 * Catches a background that throws while rendering and shows the gradient.
 *
 * This is the backstop, not the main defence: the failure that reached
 * GlitchTip (FRONTEND-WEB-USER-1U) was an async unhandled rejection from
 * `new WebGLRenderer(...)`, which no boundary can catch. The `requiresWebGL`
 * probe below is what prevents that one.
 */
class BackgroundErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <FallbackGradient /> : this.props.children;
  }
}

interface BackgroundRendererProps {
  backgroundId: string;
  props: Record<string, unknown>;
  /** From the registry entry — whether this background needs a WebGL context. */
  requiresWebGL: boolean;
}

export default function BackgroundRenderer({
  backgroundId,
  props,
  requiresWebGL,
}: BackgroundRendererProps) {
  const BackgroundComponent = lazyComponents[backgroundId];

  // `null` = not probed yet. Probing in an effect (rather than during render)
  // keeps the first client render identical to the server's.
  const [hasWebGL, setHasWebGL] = useState<boolean | null>(null);

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setHasWebGL(isWebGLAvailable());
    /* eslint-enable react-hooks/set-state-in-effect */
  }, []);

  if (!BackgroundComponent) {
    return <FallbackGradient />;
  }

  // Hold the gradient until the probe confirms a context exists. Mounting the
  // component first and letting it fail is exactly the reported bug.
  if (requiresWebGL && hasWebGL !== true) {
    return <FallbackGradient />;
  }

  return (
    <div className="absolute inset-0 w-full h-full">
      <BackgroundErrorBoundary>
        <BackgroundComponent {...props} />
      </BackgroundErrorBoundary>
    </div>
  );
}
